import React from 'react';
import { ContentRow } from '../../types/homepage';
import { BlockConfig } from './blockConfig.shared';
import {
    WelcomeBanner,
    CategoriesList,
    HighlightedCategoriesBlock,
    HighlightedChannels,
    TextOverVideo,
    FeaturedChannel,
    CustomPromo,
    SearchFilterBlock,
    QuickLinksChannels,
} from './blocks/Blocks';
import HeroCarousel from './blocks/HeroCarousel';
import MediumCarousel from './blocks/MediumCarousel';
import SmallThumbnailGrid from './blocks/SmallThumbnailGrid';
import { VerticalList, HorizontalScroll } from './blocks/MiscBlocks';
import HeaderBlock from './blocks/HeaderBlock';
import FooterBlock from './blocks/FooterBlock';
const noop = (): void => {
    return;
};

export interface BlockRendererProps {
    row: ContentRow;
}

export const BLOCK_RENDERERS: Record<string, React.FC<BlockRendererProps>> = {
    'welcome-banner': ({ row }: BlockRendererProps) => <WelcomeBanner row={row} />,
    'hero-carousel': ({ row }: BlockRendererProps) => <HeroCarousel row={row} onVideoClick={noop} />,
    'categories-list': ({ row }: BlockRendererProps) => <CategoriesList row={row} onCategoryClick={noop} />,
    'highlighted-categories': ({ row }: BlockRendererProps) => <HighlightedCategoriesBlock row={row} onCategoryClick={noop} />,
    'all-channels': ({ row }: BlockRendererProps) => <HighlightedChannels row={row} onChannelClick={noop} />,
    'featured-channel': ({ row }: BlockRendererProps) => <FeaturedChannel row={row} onVideoClick={noop} onChannelClick={noop} />,
    'medium-carousel': ({ row }: BlockRendererProps) => <MediumCarousel row={row} onVideoClick={noop} />,
    'search-filter': ({ row }: BlockRendererProps) => <SearchFilterBlock row={row} />,
    'quick-links': ({ row }: BlockRendererProps) => <QuickLinksChannels row={row} onChannelClick={noop} />,
    'text-over-video': ({ row }: BlockRendererProps) => <TextOverVideo row={row} onVideoClick={noop} />,
    'thumbnail-grid': ({ row }: BlockRendererProps) => <SmallThumbnailGrid row={row} onVideoClick={noop} />,
    'vertical-list': ({ row }: BlockRendererProps) => <VerticalList row={row} onVideoClick={noop} />,
    'horizontal-scroll': ({ row }: BlockRendererProps) => <HorizontalScroll row={row} onVideoClick={noop} />,
    'custom-promo': ({ row }: BlockRendererProps) => <CustomPromo row={row} />,
    'header': ({ row }: BlockRendererProps) => <HeaderBlock row={row} />,
    'footer': ({ row }: BlockRendererProps) => <FooterBlock row={row} />,
};

export function getBlockRenderer(type: string): React.FC<BlockRendererProps> | null {
    return BLOCK_RENDERERS[type] || null;
}

export function configToRow(type: string, config: BlockConfig, id = 'preview'): ContentRow {
    const settings: Record<string, unknown> = {
        fullWidth: config.fullWidth,
        backgroundColor: config.backgroundColor,
        textColor: config.textColor,
        backgroundImage: config.backgroundImage,
        overlayOpacity: config.overlayOpacity,
        eyebrow: config.eyebrow,
        ctaLabel: config.ctaLabel,
        ctaUrl: config.ctaUrl,
        ctaSecondaryLabel: config.ctaSecondaryLabel,
        ctaSecondaryUrl: config.ctaSecondaryUrl,
        searchPlaceholder: config.searchPlaceholder,
        dropdowns: config.dropdowns,
        showDuration: config.showDuration,
        showViewCount: config.showViewCount,
        showChannelName: config.showChannelName,
        showDescription: config.showDescription,
        autoPlay: config.autoPlay,
        tileColor: config.tileColor,
        htmlContent: config.htmlContent,
        ctaButtonLabel: config.ctaButtonLabel,
        ctaButtonUrl: config.ctaButtonUrl,
        titleColor: config.titleColor,
        subtitleColor: config.subtitleColor,
        ctaBgColor: config.ctaBgColor,
        ctaTextColor: config.ctaTextColor,
        ctaSecondaryBgColor: config.ctaSecondaryBgColor,
        ctaSecondaryTextColor: config.ctaSecondaryTextColor,
        logoText: config.logoText,
        logoImage: config.logoImage,
        logoUrl: config.logoUrl,
        navLinks: config.navLinks,
        topBarLinks: config.topBarLinks,
        showSearchInHeader: config.showSearchInHeader,
        topBarBgColor: config.topBarBgColor,
        topBarTextColor: config.topBarTextColor,
        copyrightText: config.copyrightText,
        footerColumns: config.footerColumns,
        socialLinks: config.socialLinks,
        socialTitle: config.socialTitle,
        linkColor: config.linkColor,
        socialIconColor: config.socialIconColor,
    };

    return {
        id,
        type,
        title: config.title || null,
        subtitle: config.subtitle || null,
        layoutStyle: config.layoutStyle,
        settings,
        content: config.content as any[],
    };
}

const BLOCKS_WITHOUT_CONTENT_REQUIREMENT = ['welcome-banner', 'search-filter', 'custom-promo', 'header', 'footer'];

export function BlockPreview({ type, config }: { type: string; config: BlockConfig }) {
    const Renderer = getBlockRenderer(type);

    if (!Renderer) {
        return (
            <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                {`No preview available for block type "${type}" yet.`}
            </div>
        );
    }

    const row = configToRow(type, config);
    const needsContent = !BLOCKS_WITHOUT_CONTENT_REQUIREMENT.includes(type);

    if (needsContent && (!row.content || row.content.length === 0)) {
        return (
            <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                Add some content items to see a preview.
            </div>
        );
    }

    return <Renderer row={row} />;
}